import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCreditBalance } from '../services/credits';

interface CreditBalanceProps {
  userId: string;
  onError?: (error: string) => void;
  refreshTrigger?: number; // Optional trigger to force refresh
}

export function CreditBalance({ userId, onError, refreshTrigger }: CreditBalanceProps) {
  const [credits, setCredits] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchCredits = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCreditBalance(userId);
      setCredits(data.credits);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load credits';
      onError?.(errorMessage);
      setCredits(null);
    } finally {
      setLoading(false);
    }
  }, [userId, onError]);

  useEffect(() => {
    if (userId) {
      fetchCredits();
    }
  }, [userId, refreshTrigger, fetchCredits]);

  // Expose refresh function via window for global access
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).refreshCreditBalance = fetchCredits;
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).refreshCreditBalance;
      }
    };
  }, [fetchCredits]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-text-muted">
        <div className="w-4 h-4 border-2 border-text-subtle border-t-transparent rounded-full animate-spin" />
        <span className="text-body-sm">Loading...</span>
      </div>
    );
  }

  if (credits === null) {
    return (
      <div className="flex items-center gap-2 text-text-muted">
        <span className="text-body-sm">Credits unavailable</span>
      </div>
    );
  }

  return (
    <button
      onClick={() => navigate('/upgrade')}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-hover-strong rounded-button border border-border cursor-pointer hover:bg-hover-strong/80 transition-colors"
    >
      <svg
        className="w-4 h-4 text-white"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span className="text-white font-medium text-body-sm">{credits}</span>
      <span className="text-text-muted text-body-sm">Credits</span>
    </button>
  );
}

